import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import Pagination from "@mui/material/Pagination";
import Flatpickr from "react-flatpickr";
import { authContext } from "../contexts/AuthProvider";
import { loaderContext } from "../contexts/LoaderProvider";
import Loader from "../layout/Loader";
import Header from "../layout/Header";
import Sidebar from "../layout/Sidebar";
import Footer from "../layout/Footer";
import ScrollToTop from "../layout/ScrollToTop";
import LoadScript from "../utils/LoadScript";
import { fetchTransactions } from "../services/walletService";
import socket from "../socket/socket";
import ExportTransactions from "./ExportTransactions";
import { formatDecimal } from "../utils/decimalFormat";
import TopupWallet from "./TopupWallet";

const AllTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const { isLoading, setIsLoading } = useContext(loaderContext);
  const [isAuth] = useContext(authContext);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const getTransactions = useCallback(async () => {
    setIsLoading(true);
    let startDate = dateRange[0]
      ? moment(dateRange[0]).startOf("day").toISOString()
      : null;
    let endDate = dateRange[1]
      ? moment(dateRange[1]).endOf("day").toISOString()
      : null;
    const response = await fetchTransactions(
      page,
      limit,
      search,
      startDate,
      endDate,
    );
    if (response.data.code === 1) {
      setTransactions(response.data.data);
      setTotalPages(response.data.pages);
      setIsLoading(false);
    }
  }, [page, search, limit, setIsLoading, dateRange]);

  const resetFilter = () => {
    setPage(1);
    setSearch("");
    setDateRange([null, null]);
  };

  useEffect(() => {
    if (isAuth === true) {
      setIsLoading(false);
    }
    if (isAuth === false) {
      navigate("/");
    }
  }, [isAuth, navigate, setIsLoading]);

  useEffect(() => {
    getTransactions();
    socket.on(`admin:merchantDataUpdate`, (data) => {
      if (data) {
        getTransactions();
      }
    });
    return () => {
      socket.off(`admin:merchantDataUpdate`);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getTransactions();
  }, [page, search, getTransactions]);

  const renderTransactionStatus = (status) => {
    const statusConfig = {
      pending: "bg-warning-transparent",
      success: "bg-success-transparent",
      failed: "bg-danger-transparent",
      cancelled: "bg-danger-transparent",
    };

    const badgeClass = statusConfig[status];

    if (!badgeClass) return "---";

    return (
      <span className={`badge ${badgeClass}`}>{status.toUpperCase()}</span>
    );
  };

  const renderTransactionList = (transactions) => (
    <>
      {isLoading === true ? (
        <Loader />
      ) : (
        <>
          {transactions?.length > 0 ? (
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead>
                  <tr>
                    <th scope="col">Transaction Id</th>
                    <th scope="col">Note</th>
                    <th scope="col">Type</th>
                    <th scope="col">Status</th>
                    <th scope="col">Channel</th>
                    <th scope="col">Branch</th>
                    <th scope="col">Order Id</th>
                    <th scope="col">Date</th>
                    <th scope="col">Time</th>
                    <th className="text-center" scope="col">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions?.map((transaction, index) => (
                    <tr key={index}>
                      <td>{transaction?.transactionId}</td>
                      <td>{transaction?.notes}</td>
                      <td>
                        <span
                          className={`badge bg-${
                            transaction?.type === "credit"
                              ? "success"
                              : "danger"
                          }-transparent`}
                        >
                          {transaction?.type?.toUpperCase()}
                        </span>
                      </td>
                      <td>{renderTransactionStatus(transaction?.status)}</td>
                      <td>
                        {transaction?.channel?.trim()
                          ? transaction?.channel
                          : "---"}
                      </td>
                      <td>{transaction?.branchName ?? "---"}</td>
                      <td>{transaction?.orderId ?? "---"}</td>
                      <td>
                        {moment(transaction?.createdAt)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("Do MMM, YYYY")}
                      </td>
                      <td>
                        {moment(transaction?.createdAt)
                          .tz(process.env.REACT_APP_TIMEZONE)
                          .format("hh:mm A")}
                      </td>
                      <td className="text-center">
                        <div className="btn-list">
                          <button
                            className={`btn btn-sm btn-${
                              transaction?.type === "credit"
                                ? "success"
                                : "danger"
                            }-light btn-wave waves-effect waves-light`}
                          >
                            <i
                              className={`ri-${
                                transaction?.type === "credit"
                                  ? "add"
                                  : "subtract"
                              }-line me-1`}
                            ></i>
                            {formatDecimal(transaction?.amountInt)}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="10">
                      <div className="d-flex justify-content-end">
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, value) => setPage(value)}
                        />
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center my-5">
              <img
                className="mx-auto w-25 my-5"
                src={`${process.env.REACT_APP_PUBLIC_URL}assets/images/media/backgrounds/no-content.png`}
                alt="no-content"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div className="page">
        <Header />
        <Sidebar />
        <div className="main-content app-content">
          <div className="container-fluid">
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card custom-card">
                  <div className="card-header justify-content-between">
                    <div className="card-title">
                      <div className="d-flex justify-content-between align-items-center px-3 py- gap-2">
                        <span className="d-block fw-medium fs-15">
                          Transctions
                        </span>
                      </div>
                    </div>
                    <div className="d-block d-md-flex flex-wrap gap-2">
                      <div>
                        <input
                          className="form-control form-control-md mb-2"
                          type="text"
                          placeholder="Search By Transaction Id"
                          aria-label=".form-control-sm example"
                          onChange={(e) => setSearch(e.target.value)}
                          value={search}
                        />
                      </div>
                      <div>
                        <Flatpickr
                          className="form-control form-control-md mb-2"
                          placeholder="Search by date range"
                          options={{ mode: "range", dateFormat: "d-m-Y" }}
                          value={dateRange}
                          onChange={(dates) => {
                            if (dates.length === 2) {
                              setDateRange(dates);
                            }
                          }}
                        />
                      </div>
                      <div>
                        <button
                          onClick={resetFilter}
                          className="btn btn-danger btn-md w-100 w-md-auto mb-2"
                        >
                          <i className="ri-tools-line me-1 fw-medium align-middle"></i>
                          Reset
                        </button>
                      </div>
                      <div>
                        <ExportTransactions
                          search={search}
                          dateRange={dateRange}
                        />
                      </div>
                      <div>
                        <TopupWallet getTransactions={getTransactions} />
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {renderTransactionList(transactions)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
      <ScrollToTop />
      <LoadScript />
    </>
  );
};

export default AllTransactions;
